import type { CityItem, MunicipalityRegistryResponse, RegistryCityItem } from '@buzzmap/shared';

/**
 * Shared client-side registry lookup (T011-FE).
 *
 * `GET /api/cities` is the only authority the client uses to turn a stored
 * comune id (or an exact name+province pair) into registry data — it replaces
 * the retired `/api/cities/nearest` name lookup and the static
 * `italianCities` coordinates ("static-city authorities", plan l.138). The
 * response is fetched once per session and cached in this module so
 * `DiscoveryProvider` (hints), the v1→v2 migration and the saved-comune
 * projection all share ONE request.
 *
 * Never fuzzy, never first-hit: a pair that does not match uniquely is
 * unresolved, and the caller reports a confirmation/invalid outcome instead.
 */

let registryPromise: Promise<MunicipalityRegistryResponse> | null = null;

/** Bound the registry fetch: a hang must not hold the v2 entry gate forever (S5). */
export const REGISTRY_TIMEOUT_MS = 12000;

/** Runtime guard for the registry body (a proxy/error page must not slip through). */
function isRegistryResponse(v: unknown): v is MunicipalityRegistryResponse {
  if (!v || typeof v !== 'object') return false;
  return Array.isArray((v as { cities?: unknown }).cities);
}

/** Fetch (or reuse) the municipality registry. A failure clears the cache so a later lookup retries. */
export function loadRegistry(): Promise<MunicipalityRegistryResponse> {
  if (!registryPromise) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REGISTRY_TIMEOUT_MS);
    registryPromise = fetch('/api/cities', { signal: controller.signal })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const body: unknown = await r.json();
        if (!isRegistryResponse(body)) throw new Error('malformed registry body');
        return body;
      })
      .catch((err: unknown) => {
        registryPromise = null;
        throw err;
      })
      .finally(() => clearTimeout(timer));
  }
  return registryPromise;
}

/** Drop the cached registry (tests; a registry-version change is T016's cache eviction). */
export function resetRegistryCache(): void {
  registryPromise = null;
}

/** Exact pair match against a registry listing — never fuzzy, never first-hit. */
export function matchRegistryPair(
  cities: RegistryCityItem[],
  hint: { city: string; province?: string }
): RegistryCityItem[] {
  const name = hint.city.trim().toLowerCase();
  const province = hint.province?.trim().toLowerCase();
  return cities.filter((c) => {
    if (c.name.trim().toLowerCase() !== name) return false;
    if (!province) return true;
    return (
      c.province.toLowerCase() === province || (c.provinceName ?? '').trim().toLowerCase() === province
    );
  });
}

/**
 * Resolve an exact name(+province) pair to exactly one registry row.
 * Returns null when the registry cannot be read, when there is no match, or
 * when a province-less homonym matches more than one comune — the caller turns
 * that into "needs confirmation", never into a choice.
 */
export async function resolveRegistryPair(
  city: string,
  province?: string
): Promise<RegistryCityItem | null> {
  const registry = await loadRegistry();
  const matches = matchRegistryPair(registry.cities ?? [], { city, province });
  if (matches.length !== 1) return null;
  const hit = matches[0];
  return hit && hit.path ? hit : null;
}

/** Look up a saved `SavedLocationV2` municipality id in the registry. */
export async function findRegistryById(municipalityId: string): Promise<RegistryCityItem | null> {
  const registry = await loadRegistry();
  return (registry.cities ?? []).find((c) => c.id === municipalityId) ?? null;
}

/** Registry row → the v1 `CityItem` projection old callers still read (read-only, T011). */
export function registryCityToItem(c: RegistryCityItem): CityItem {
  const item: CityItem = { name: c.name, lat: c.lat, lng: c.lng };
  if (c.province) item.province = c.province;
  return item;
}
